import { useEffect, useMemo, useRef } from "react";
import { resolveIcon } from "../../constants.js";
import { toISO } from "../../utils/date.js";

export default function TimelineCalendar({ selectedDate, onSelect, items, allDays, legForDate, categories, darkMode = false, colWidth = 77, scrollTrigger }) {
  const COL_WIDTH = colWidth;
  const ROW_HEIGHT = 22;
  const ROW_GAP = 4;
  const HEADER_HEIGHT = 56;
  const containerRef = useRef(null);

  const dateIndexRef = useRef({});
  const selectedDateRef = useRef(selectedDate);
  selectedDateRef.current = selectedDate;

  const { rows, dateIndex } = useMemo(() => {
    const idx = {};
    allDays.forEach((d, i) => { idx[toISO(d)] = i; });

    const getEndIso = (it) => (
      it.endDate && it.endDate > it.date && idx[it.endDate] !== undefined
        ? it.endDate
        : it.date
    );
    const getDuration = (it) => idx[getEndIso(it)] - idx[it.date];
    const getLayoutBounds = (it) => {
      const startCol = idx[it.date];
      const endCol = idx[getEndIso(it)];
      if (it.category === "accom") {
        return {
          start: startCol + 0.5,
          end: Math.max(endCol + 0.5, startCol + 1),
        };
      }
      return {
        start: startCol,
        end: endCol + 1,
      };
    };

    const sorted = Object.values(items)
      .filter((it) => idx[it.date] !== undefined)
      .sort((a, b) => {
        const aIsAccom = a.category === "accom";
        const bIsAccom = b.category === "accom";
        if (aIsAccom !== bIsAccom) return aIsAccom ? -1 : 1;
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        const aDur = getDuration(a);
        const bDur = getDuration(b);
        return bDur - aDur;
      });

    const rowEnds = [];
    const placed = sorted.map((it) => {
      const startCol = idx[it.date];
      const endIso = getEndIso(it);
      const endCol = idx[endIso];
      const layout = getLayoutBounds(it);
      let row = 0;
      while (rowEnds[row] !== undefined && rowEnds[row] > layout.start) row++;
      rowEnds[row] = layout.end;
      return {
        ...it,
        _startCol: startCol,
        _endCol: endCol,
        _row: row,
        _layoutStart: layout.start,
        _layoutEnd: layout.end,
      };
    });
    return { rows: placed, dateIndex: idx };
  }, [items, allDays]);
  dateIndexRef.current = dateIndex;

  const totalRows = rows.reduce((m, r) => Math.max(m, r._row + 1), 0);
  const trackHeight = Math.max(totalRows * (ROW_HEIGHT + ROW_GAP), ROW_HEIGHT);
  const totalWidth = allDays.length * COL_WIDTH;

  useEffect(() => {
    if (!containerRef.current) return;
    const col = dateIndexRef.current[selectedDateRef.current];
    if (col === undefined) return;
    const container = containerRef.current;
    const target = col * COL_WIDTH - container.clientWidth / 2 + COL_WIDTH / 2;
    container.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [scrollTrigger, COL_WIDTH]);

  return (
    <div className="border-b border-slate-200/70 dark:border-slate-700 bg-gradient-to-b from-white dark:from-slate-800 to-slate-50/50 dark:to-slate-900/50">
      <div
        ref={containerRef}
        className="overflow-x-auto overflow-y-hidden"
        style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
      >
        <div style={{ width: totalWidth, position: "relative" }} className="select-none">
          <div className={`flex sticky top-0 ${darkMode ? "bg-slate-800/95" : "bg-white/95"} backdrop-blur z-10 border-b border-slate-200/70 dark:border-slate-700`}>
            {allDays.map((d, i) => {
              const iso = toISO(d);
              const leg = legForDate(d);
              const selected = iso === selectedDate;
              const isFirstOfLeg = leg && iso === leg.start;
              return (
                <button
                  key={iso}
                  onClick={() => onSelect(iso)}
                  className="flex-shrink-0 flex flex-col items-center justify-center py-2 transition-colors relative"
                  style={{
                    width: COL_WIDTH,
                    height: HEADER_HEIGHT,
                    background: selected ? `${leg?.colour || "#0f172a"}15` : "transparent",
                    borderLeft: i === 0 ? "none" : `1px solid ${leg ? `${leg.colour}15` : "#f1f5f9"}`,
                  }}
                >
                  <span className="text-[9px] uppercase tracking-wider text-slate-400 dark:text-slate-500 leading-none">
                    {d.toLocaleDateString("en-GB", { weekday: "short" })}
                  </span>
                  <span
                    className="text-base font-semibold leading-none mt-1"
                    style={{
                      fontFamily: "'Fraunces', Georgia, serif",
                      color: selected ? (leg?.colour || "#0f172a") : darkMode ? "#f1f5f9" : "#0f172a",
                    }}
                  >
                    {d.getDate()}
                  </span>
                  {isFirstOfLeg && (
                    <span className="text-[10px] mt-0.5 leading-none">{leg.emoji}</span>
                  )}
                  {selected && (
                    <span
                      className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full"
                      style={{ background: leg?.colour || "#0f172a" }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          <div className="relative" style={{ height: trackHeight + 12, padding: "6px 0" }}>
            {allDays.map((d, i) => {
              const iso = toISO(d);
              const leg = legForDate(d);
              const selected = iso === selectedDate;
              return (
                <button
                  key={`col-${iso}`}
                  onClick={() => onSelect(iso)}
                  className="absolute top-0 bottom-0 transition-colors"
                  style={{
                    left: i * COL_WIDTH,
                    width: COL_WIDTH,
                    background: selected ? `${leg?.colour || "#0f172a"}08` : "transparent",
                    borderLeft: i === 0 ? "none" : `1px solid ${leg ? `${leg.colour}12` : "#f1f5f9"}`,
                  }}
                />
              );
            })}

            {rows.map((it) => {
              const cat = categories.find((c) => c.id === it.category) || { colour: "#64748b", icon: "sparkles" };
              const Icon = resolveIcon(cat.icon);
              const span = it._endCol - it._startCol + 1;
              const left = it._layoutStart * COL_WIDTH + 3;
              const width = (it._layoutEnd - it._layoutStart) * COL_WIDTH - 6;
              const top = 6 + it._row * (ROW_HEIGHT + ROW_GAP);
              const isMultiDay = span > 1;
              const isBooked = it.status === "booked";
              return (
                <button
                  key={it.id}
                  onClick={() => onSelect(it.date)}
                  className="absolute rounded-md flex items-center gap-1 px-1.5 text-[11px] font-medium leading-none truncate hover:brightness-110 transition-all hover:shadow-sm"
                  style={{
                    left,
                    width,
                    top,
                    height: ROW_HEIGHT,
                    background: isBooked ? cat.colour : `${cat.colour}20`,
                    color: isBooked ? "white" : cat.colour,
                    border: isBooked ? "none" : `1px solid ${cat.colour}50`,
                  }}
                  title={`${it.title}${isMultiDay ? ` (${span} days)` : ""}`}
                >
                  <Icon size={10} className="flex-shrink-0" />
                  <span className="truncate">{it.title}</span>
                </button>
              );
            })}

            {totalRows === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400 dark:text-slate-500">
                No items yet — tap a day below to add something
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
